import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import FieldLabel from 'components/Form/Field/FieldLabel';
import InputEmail from 'components/Form/InputEmail';
import SubmitButton from 'components/Form/SubmitButton';
import SideBySide from 'components/Layout/SideBySide';
import ModalDestructiveConfirm from 'components/ModalDestructiveConfirm';
import SectionHeader from 'components/SectionHeader';
import TooltipWrapper from 'components/Tooltip';
import InboxLayout from 'pages/Portal/components/Inbox/InboxLayout';
import InboxTableLayout from 'pages/Portal/components/Inbox/TableLayout';
import {PortalPermissions} from 'pages/Portal/components/Inbox/types';
import Stroke from 'pages/Portal/components/Stroke';
import {useBackend} from 'state/backend';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';
import isEmail from 'validator/lib/isEmail';
import useEditClientInboxUsers from './services';
import {UserFormFields} from './types';

type ClientInboxUsersEditBackendData = {
    clientName: string;
    clientUsers: string[];
    email: string;
    maxUsers: number;
    partnerName: string;
    permissions: PortalPermissions;
    portalMid: string;
    sessionExpiration: number;
};

export type ClientInboxUsersEditBackend =
    Backend<ClientInboxUsersEditBackendData>;

type UserFormData = FormFields<UserFormFields>;

const cleanFormData = (data: UserFormData): UserFormData => {
    const cleaned = {...data};
    cleaned.newUsers = cleaned.newUsers.filter(
        (user: string): boolean => user.length > 0,
    );

    return cleaned;
};

const ClientInboxUsersEdit: FC = () => {
    const backendData = useBackend<ClientInboxUsersEditBackend>();
    const {
        clientName,
        clientUsers,
        email,
        maxUsers,
        partnerName,
        permissions,
        portalMid,
        sessionExpiration,
    } = backendData.data;
    const {t} = useTranslation('Portal');
    const originalUsers: string[] = [...clientUsers];

    const methods = useForm<UserFormData>({
        defaultValues: {
            newUsers: originalUsers,
            originalUsers,
        },
    });
    const {getValues, watch} = methods;
    watch('newUsers');

    const {mutateAsync} = useEditClientInboxUsers();
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const hasAtLeastOneEmailSet = () =>
        cleanFormData(getValues()).newUsers.length > 0;

    const onSubmit = (formData: UserFormFields, isModalSubmit: boolean) => {
        const cleanedData = cleanFormData(formData);
        const hasPortalEmail = cleanedData.newUsers.includes(email);

        if (!hasPortalEmail && !isModalSubmit) {
            setIsConfirmModalOpen(true);
        } else {
            mutateAsync({formData: cleanedData, portalMid})
                .then(
                    onApiSuccess({
                        onSuccess: () =>
                            setUrl(
                                isModalSubmit
                                    ? '/portal/logout'
                                    : `/portal/${portalMid}/client-inbox/users/`,
                            ),
                    }),
                )
                .catch(onApiError(methods));
        }
    };

    const tooltipProps = {
        message: t('clientInboxUsers.fieldTooltip'),
    };

    const fieldLabel = (
        <FieldLabel isSideBySide={true} tooltip={tooltipProps}>
            {t('clientInboxUsers.fieldLabel')}
        </FieldLabel>
    );

    return (
        <InboxLayout
            clientName={clientName}
            email={email}
            flags={backendData.flags}
            navigation="client-inbox-users"
            partnerName={partnerName}
            permissions={permissions}
            portalMid={portalMid}
            sessionExpiration={sessionExpiration}
        >
            <SectionHeader
                level={1}
                subtitle={t('clientInboxUsers.viewSubtitle', {
                    partnerName,
                })}
                title={t('clientInboxUsers.viewTitle')}
            />
            <InboxTableLayout>
                <FormProvider {...methods}>
                    <form
                        onSubmit={methods.handleSubmit(() => {
                            onSubmit(methods.getValues(), false);
                        })}
                    >
                        <div className="flex w-full justify-between">
                            <div className="flex">
                                <Stroke />
                                <div className="text-grey-800 font-semibold whitespace-break-spaces break-words text-base min-w-0">
                                    {t('clientInboxUsers.cardTitle')}
                                </div>
                            </div>
                            <TooltipWrapper
                                message={
                                    hasAtLeastOneEmailSet()
                                        ? ''
                                        : t('clientInboxUsers.cardEditSubtext')
                                }
                            >
                                <SubmitButton
                                    aria-label="save"
                                    disabled={!hasAtLeastOneEmailSet()}
                                    kind="primary"
                                    size="sm"
                                >
                                    {t('save')}
                                </SubmitButton>
                            </TooltipWrapper>
                            <ModalDestructiveConfirm
                                header={t('clientInboxUsers.warningTitle')}
                                isOpen={isConfirmModalOpen}
                                onCancel={() => {
                                    setIsConfirmModalOpen(false);
                                }}
                                onConfirm={() => {
                                    onSubmit(methods.getValues(), true);
                                }}
                                submit={t('clientInboxUsers.warningSubmit')}
                            >
                                <p className="text-sm">
                                    {t('clientInboxUsers.warningStart', {
                                        email,
                                    })}
                                </p>
                                <ul className="my-6 text-sm">
                                    <li>
                                        {t(
                                            'clientInboxUsers.warningExplanation1',
                                        )}
                                    </li>
                                    <li>
                                        <Trans
                                            components={[<b key={0}> </b>]}
                                            i18nKey="clientInboxUsers.warningExplanation2"
                                            ns="Portal"
                                            t={t}
                                        />
                                    </li>
                                </ul>
                                <p className="text-sm">
                                    {t('clientInboxUsers.warningEnd', {email})}
                                </p>
                            </ModalDestructiveConfirm>
                        </div>
                        <div className="flex flex-col w-[62rem]">
                            <SideBySide className="max-w-lg" spacing="sm">
                                {[...Array(maxUsers).keys()].map((index) => (
                                    <InputEmail
                                        key={`user-email-input-${index}`}
                                        className="mb-0"
                                        label={index === 0 ? fieldLabel : ''}
                                        name={`newUsers.${index}`}
                                        placeholder="example@domain.com"
                                        rules={{
                                            required: false,
                                            validate: (value: string) => {
                                                if (value) {
                                                    if (!isEmail(value)) {
                                                        return t(
                                                            'form:error.emailInvalid',
                                                        );
                                                    }

                                                    if (
                                                        cleanFormData(
                                                            getValues(),
                                                        ).newUsers.filter(
                                                            (newEmail) =>
                                                                newEmail ===
                                                                value,
                                                        ).length > 1
                                                    ) {
                                                        return t(
                                                            'clientInboxUsers.formErrorEmailDuplicate',
                                                        );
                                                    }
                                                }

                                                return undefined;
                                            },
                                        }}
                                    />
                                ))}
                            </SideBySide>
                        </div>
                    </form>
                </FormProvider>
            </InboxTableLayout>
        </InboxLayout>
    );
};

export default ClientInboxUsersEdit;
